import React, { useState } from "react";
import { Box, Flex, Icon, IconButton, Image, Link, Progress, Text, useColorModeValue } from "@chakra-ui/react";
import {
  MdDescription,
  MdDownload,
  MdFolderZip,
  MdHideSource,
  MdInsertDriveFile,
  MdMic,
  MdPictureAsPdf,
  MdSlideshow,
  MdTableChart,
} from "react-icons/md";
import { extensionDe, formatoDuracion, formatoTamano } from "./archivos";

const ICONOS = {
  pdf: { icono: MdPictureAsPdf, color: "red.400" },
  doc: { icono: MdDescription, color: "blue.400" },
  docx: { icono: MdDescription, color: "blue.400" },
  odt: { icono: MdDescription, color: "blue.400" },
  rtf: { icono: MdDescription, color: "blue.400" },
  txt: { icono: MdDescription, color: "gray.400" },
  xls: { icono: MdTableChart, color: "green.400" },
  xlsx: { icono: MdTableChart, color: "green.400" },
  ods: { icono: MdTableChart, color: "green.400" },
  csv: { icono: MdTableChart, color: "green.400" },
  ppt: { icono: MdSlideshow, color: "orange.400" },
  pptx: { icono: MdSlideshow, color: "orange.400" },
  odp: { icono: MdSlideshow, color: "orange.400" },
  zip: { icono: MdFolderZip, color: "yellow.500" },
  rar: { icono: MdFolderZip, color: "yellow.500" },
  "7z": { icono: MdFolderZip, color: "yellow.500" },
};

function Documento({ adjunto, propio }) {
  const fondo = useColorModeValue(propio ? "whiteAlpha.300" : "white", "whiteAlpha.200");
  const extension = extensionDe(adjunto.nombre);
  const { icono, color } = ICONOS[extension] || { icono: MdInsertDriveFile, color: "gray.400" };
  return (
    <Flex align="center" gap="10px" bg={fondo} borderRadius="10px" p="8px" minW="200px" maxW="280px">
      <Icon as={icono} boxSize="30px" color={propio ? "white" : color} flexShrink={0} />
      <Box flex="1" minW={0}>
        {adjunto.url ? (
          <Link href={adjunto.url} isExternal fontSize="sm" fontWeight="600" noOfLines={1} title={adjunto.nombre}>{adjunto.nombre}</Link>
        ) : (
          <Text fontSize="sm" fontWeight="600" noOfLines={1} title={adjunto.nombre}>{adjunto.nombre}</Text>
        )}
        <Text fontSize="11px" opacity={0.8}>{formatoTamano(adjunto.tamano)}{extension ? ` · ${extension.toUpperCase()}` : ""}</Text>
      </Box>
      {adjunto.urlDescarga && (
        <IconButton
          as="a"
          href={adjunto.urlDescarga}
          aria-label={`Descargar ${adjunto.nombre}`}
          icon={<MdDownload />}
          size="sm"
          variant="ghost"
          color="inherit"
          flexShrink={0}
        />
      )}
    </Flex>
  );
}

/**
 * Lo que trae un mensaje: foto, audio, video o documento. Mientras sube (`local`) se ve
 * con su vista previa y el avance; vencido queda solo el aviso.
 */
export default function Adjunto({ mensaje, propio }) {
  const { adjunto } = mensaje;
  // Un formato que el navegador no muestra (HEIC en Chrome) o un enlace que ya venció.
  const [noSeMuestra, setNoSeMuestra] = useState(false);

  if (adjunto.vencido) {
    return (
      <Flex align="center" gap="6px" fontSize="xs" fontStyle="italic" opacity={0.85}>
        <Icon as={MdHideSource} boxSize="16px" />
        <Text>Archivo vencido{adjunto.nombre ? ` · ${adjunto.nombre}` : ""}</Text>
      </Flex>
    );
  }

  const subiendo = mensaje.estado === "subiendo";
  let contenido;
  if (adjunto.categoria === "imagen" && adjunto.url && !noSeMuestra) {
    contenido = (
      <Image
        src={adjunto.url}
        alt={adjunto.nombre}
        maxH="260px"
        maxW="100%"
        minW="80px"
        borderRadius="10px"
        objectFit="cover"
        cursor={adjunto.local ? "default" : "zoom-in"}
        onClick={() => !adjunto.local && window.open(adjunto.url, "_blank", "noopener")}
        onError={() => setNoSeMuestra(true)}
      />
    );
  } else if (adjunto.categoria === "audio" && adjunto.url && !noSeMuestra) {
    contenido = (
      <Flex align="center" gap="6px">
        {/^nota-de-voz/.test(adjunto.nombre) && <Icon as={MdMic} boxSize="18px" flexShrink={0} />}
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <audio controls preload="metadata" src={adjunto.url} style={{ width: "230px", maxWidth: "100%" }} onError={() => setNoSeMuestra(true)} />
        {adjunto.duracion ? <Text fontSize="11px" opacity={0.8}>{formatoDuracion(adjunto.duracion)}</Text> : null}
      </Flex>
    );
  } else if (adjunto.categoria === "video" && adjunto.url && !noSeMuestra) {
    contenido = (
      // eslint-disable-next-line jsx-a11y/media-has-caption
      <video controls preload="metadata" src={adjunto.url} style={{ maxHeight: "260px", maxWidth: "100%", borderRadius: "10px" }} onError={() => setNoSeMuestra(true)} />
    );
  } else {
    contenido = <Documento adjunto={adjunto} propio={propio} />;
  }

  return (
    <Box mb={mensaje.texto ? "6px" : 0}>
      {contenido}
      {subiendo && (
        <Box mt="6px">
          <Progress value={Math.round((mensaje.progreso || 0) * 100)} size="xs" borderRadius="full" colorScheme={propio ? "whiteAlpha" : "brand"} />
          <Text fontSize="11px" mt="2px" opacity={0.85}>Subiendo… {Math.round((mensaje.progreso || 0) * 100)}%</Text>
        </Box>
      )}
    </Box>
  );
}
